/**
 * Constantes de IDs de Jornadas do sistema
 * Cada jornada representa um tipo diferente de desk/negócio
 *
 * IMPORTANTE: O campo `type` da tabela `types_status_desk` é usado para filtrar
 * os status disponíveis para cada jornada no dropdown de status.
 */

export const JORNADA_IDS = {
  /** Jornada 1 - Relacionamento de Mídia (PI, propostas comerciais) */
  RELACIONAMENTO_MIDIA: 1,

  /** Jornada 2 - Criação (Jobs de design, criação) */
  CRIACAO: 2,

  /** Jornada 3 - Performance (Campanhas de mídia digital) */
  PERFORMANCE: 3,

  /** Jornada 4 - Negócios Não-Mídia (Consultoria, Tech, etc) */
  NAO_MIDIA: 4,

  /** Jornada 5 - Produção (Audiovisual, vídeos) */
  PRODUCAO: 5,

  /** Jornada 6 - Campanhas Publicitárias (Plano de mídia) */
  CAMPANHAS_PUBLICITARIAS: 6,

  /** Jornada 7 - Criação Stakeholder (Jobs de criação com fluxo próprio) */
  CRIACAO_STAKEHOLDER: 7,

  /** Jornada 8 - Faturamento (Billing de campanhas publicitárias) */
  FATURAMENTO: 8,
} as const;

export type JornadaId = (typeof JORNADA_IDS)[keyof typeof JORNADA_IDS];

/**
 * Mapeamento de type_desk_id para jornada_id
 * Define qual jornada cada tipo de desk pertence
 *
 * type_desk_id (types_desks):
 * - 1: ADS (Mídia) → Jornada 1
 * - 4: Representação → Jornada 1
 * - 2: Tech → Jornada 4
 * - 5: Sette Go On → Jornada 4
 * - 6: Ad Desk → Jornada 4
 * - 7: Consultoria → Jornada 4
 * - 8: Outros Não-Mídia → Jornada 4
 * - 11: Eventos → Jornada 4
 * - 14: Kuma Studios → Jornada 4
 * - (Jobs de Criação não usam type_desk_id, usam jornada_id diretamente)
 * - (Performance não usa type_desk_id, usa jornada_id diretamente)
 */
export const TYPE_DESK_TO_JORNADA: Record<number, JornadaId> = {
  // Relacionamento de Mídia (Jornada 1)
  1: JORNADA_IDS.RELACIONAMENTO_MIDIA, // ADS (Mídia)
  4: JORNADA_IDS.RELACIONAMENTO_MIDIA, // Representação

  // Negócios Não-Mídia (Jornada 4)
  2: JORNADA_IDS.NAO_MIDIA, // Tech
  5: JORNADA_IDS.NAO_MIDIA, // Sette Go On
  6: JORNADA_IDS.NAO_MIDIA, // Ad Desk
  7: JORNADA_IDS.NAO_MIDIA, // Consultoria
  8: JORNADA_IDS.NAO_MIDIA, // Outros Não-Mídia
  11: JORNADA_IDS.NAO_MIDIA, // Eventos
  14: JORNADA_IDS.NAO_MIDIA, // Kuma Studios
};

/**
 * Mapeamento de status.type (types_status_desk.type) para jornada_id
 * O campo `type` na tabela types_status_desk indica a qual jornada o status pertence
 *
 * types_status_desk.type:
 * - type 1: Status de Relacionamento de Mídia (Jornada 1)
 * - type 2: Status de Negócios Não-Mídia (Jornada 4)
 * - type 3: Status de Criação/Jobs (Jornada 2)
 * - type 4: Status de Performance (Jornada 3)
 * - type 5: Status de Produção (Jornada 5)
 * - type 6: Status de Campanhas Publicitárias (Jornada 6)
 * - type 7: Status de Criação Stakeholder (Jornada 7)
 */
export const STATUS_TYPE_TO_JORNADA: Record<number, JornadaId> = {
  1: JORNADA_IDS.RELACIONAMENTO_MIDIA, // Status type 1 → Jornada 1
  2: JORNADA_IDS.NAO_MIDIA, // Status type 2 → Jornada 4
  3: JORNADA_IDS.CRIACAO, // Status type 3 → Jornada 2
  4: JORNADA_IDS.PERFORMANCE, // Status type 4 → Jornada 3
  5: JORNADA_IDS.CRIACAO_STAKEHOLDER, // Status type 5 → Jornada 7
  6: JORNADA_IDS.CAMPANHAS_PUBLICITARIAS, // Status type 6 → Jornada 6
  7: JORNADA_IDS.PRODUCAO, // Status type 7 → Jornada 5
  8: JORNADA_IDS.FATURAMENTO, // Status type 8 → Jornada 8
};

/**
 * Mapeamento inverso: jornada_id para status.type
 * Usado para filtrar os status corretos ao criar/editar desks
 */
export const JORNADA_TO_STATUS_TYPE: Record<JornadaId, number> = {
  [JORNADA_IDS.RELACIONAMENTO_MIDIA]: 1,
  [JORNADA_IDS.CRIACAO]: 3,
  [JORNADA_IDS.PERFORMANCE]: 4,
  [JORNADA_IDS.NAO_MIDIA]: 2,
  [JORNADA_IDS.PRODUCAO]: 7,
  [JORNADA_IDS.CAMPANHAS_PUBLICITARIAS]: 6,
  [JORNADA_IDS.CRIACAO_STAKEHOLDER]: 5,
  [JORNADA_IDS.FATURAMENTO]: 8,
};

/**
 * Mapeamento de status_id por jornada
 * Usado para agrupar desks nos Overviews
 *
 * JORNADA 1 - RELACIONAMENTO DE MÍDIA (status type = 1):
 * - 1: Aprovação de Proposta
 * - 2: Aguardando PI
 * - 18: Grande Chance
 * - 3: Implementação
 * - 4: Ativa
 * - 69: Ativo com Ajuste
 * - 5: Checking
 * - 6: Faturamento
 * - 7: Pago
 * - 8: Perdida
 * - 9: Finalizada
 * - 10: PDI Aberto
 * - 20: IPR Aberto
 *
 * JORNADA 2 - CRIAÇÃO/JOBS (status type = 3):
 * - 21: Em Análise
 * - 22: Analisado
 * - 23: Fazendo
 * - 24: Aprovação Interna
 * - 25: Ajuste
 * - 26: Aprovação Externa
 * - 27: Aprovado
 * - 28: Em Entrega
 * - 29: Entregue
 * - 70: Cancelado
 *
 * JORNADA 3 - PERFORMANCE (status type = 4):
 * - 30: Em Análise
 * - 31: Analisado
 * - 32: Em Planejamento
 * - 33: Planning em Aprovação
 * - 34: Em Implementação
 * - 35: Ativa
 * - 36: Finalizada
 *
 * JORNADA 4 - NEGÓCIOS NÃO-MÍDIA (status type = 2):
 * - 11: Em Aberto
 * - 12: Média Chance
 * - 13: Grande Chance
 * - 14: Em Implementação
 * - 17: Ativo
 * - 15: Faturamento
 * - 16: Finalizado
 *
 * JORNADA 5 - PRODUÇÃO (status type = 7):
 * - 59: Em Briefing
 * - 60: Briefing Recebido
 * - 61: Em Orçamento
 * - 62: Orçamento Aprovado
 * - 63: Em Produção
 * - 64: Produção Finalizada
 * - 65: Em Entrega
 * - 66: Entregue
 * - 67: Faturamento
 * - 68: Pago
 *
 * JORNADA 7 - CRIAÇÃO STAKEHOLDER (status type = 5):
 * - 71: Pequena chance
 * - 72: Média chance
 * - 73: Grande chance
 * - 37: Em Análise
 * - 38: Analisado
 * - 39: Arte em elaboração
 * - 40: Aguardando Aprovação
 * - 41: Ajuste
 * - 42: Aprovado
 * - 43: Aguardando produção
 * - 44: Em produção
 * - 45: Em retirada
 * - 46: Finalizado
 */
export const STATUS_POR_JORNADA = {
  [JORNADA_IDS.RELACIONAMENTO_MIDIA]: {
    emAberto: [1, 2, 18],
    ativos: [3, 4, 69],
    checking: [5],
    faturamento: [6, 7],
    finalizados: [9, 10, 20],
    todos: [1, 2, 18, 3, 4, 69, 5, 6, 7, 9, 10, 20, 8],
  },
  [JORNADA_IDS.CRIACAO]: {
    emBriefing: [21, 22],
    ativos: [23, 24, 25],
    aprovacao: [26, 27],
    finalizacao: [28, 29, 70],
    todos: [21, 22, 23, 24, 25, 26, 27, 28, 29, 70],
  },
  [JORNADA_IDS.PERFORMANCE]: {
    emBriefing: [30, 31],
    planejamento: [32, 33],
    ativos: [34, 35],
    finalizados: [36],
    todos: [30, 31, 32, 33, 34, 35, 36],
  },
  [JORNADA_IDS.NAO_MIDIA]: {
    abertos: [11, 12, 13],
    ativos: [14, 17],
    faturamento: [15, 16],
    todos: [11, 12, 13, 14, 17, 15, 16],
  },
  [JORNADA_IDS.PRODUCAO]: {
    emBriefing: [59, 60],
    emOrcamento: [61, 62],
    ativos: [63, 64],
    emEntrega: [65, 66],
    faturamento: [67, 68],
    todos: [59, 60, 61, 62, 63, 64, 65, 66, 67, 68],
  },
  [JORNADA_IDS.CRIACAO_STAKEHOLDER]: {
    emAberto: [71, 72, 73],
    emBriefing: [37, 38],
    ativos: [39, 40, 41, 87],
    aprovacao: [42, 43],
    finalizacao: [44, 45, 46],
    todos: [71, 72, 73, 37, 38, 39, 40, 41, 87, 42, 43, 44, 45, 46],
  },
  [JORNADA_IDS.FATURAMENTO]: {
    checkingEmAndamento: [74, 75],
    checkingAprovado: [76, 77],
    emFaturamento: [78, 79, 80],
    emCobranca: [81, 82],
    repasse: [83, 84, 85],
    todos: [74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85],
  },
} as const;

/**
 * Labels das jornadas para exibição
 */
export const JORNADA_LABELS = {
  [JORNADA_IDS.RELACIONAMENTO_MIDIA]: "Relacionamento de Mídia",
  [JORNADA_IDS.CRIACAO]: "Criação",
  [JORNADA_IDS.PERFORMANCE]: "Performance",
  [JORNADA_IDS.NAO_MIDIA]: "Negócios Não-Mídia",
  [JORNADA_IDS.PRODUCAO]: "Produção",
  [JORNADA_IDS.CAMPANHAS_PUBLICITARIAS]: "Campanhas Publicitárias",
  [JORNADA_IDS.CRIACAO_STAKEHOLDER]: "Criação Stakeholder",
  [JORNADA_IDS.FATURAMENTO]: "Faturamento",
} as const;

/**
 * Função auxiliar para obter a jornada_id baseado no status_id
 * Útil para migração de desks antigos
 */
export function getJornadaByStatusId(statusId: number): JornadaId | null {
  for (const [jornadaId, grupos] of Object.entries(STATUS_POR_JORNADA)) {
    if ((grupos.todos as readonly number[]).includes(statusId)) {
      return Number(jornadaId) as JornadaId;
    }
  }
  return null;
}

/**
 * Função auxiliar para obter a jornada_id baseado no type_desk_id
 */
export function getJornadaByTypeDeskId(typeDeskId: number): JornadaId | null {
  return TYPE_DESK_TO_JORNADA[typeDeskId] || null;
}

/**
 * Função para obter o status_type correto baseado na jornada_id
 * Usado para filtrar status no dropdown de criação/edição de desks
 */
export function getStatusTypeByJornada(jornadaId: JornadaId): number {
  return JORNADA_TO_STATUS_TYPE[jornadaId];
}
